import { useState } from "react"
import { Box, Paper, Typography, useTheme } from "@mui/material"

/**
 * Hours over the range, as columns.
 *
 * ── Why a chart earns its place next to the table ───────────────────────────
 *
 * The table answers "how much"; this answers "what shape". Thirty-two hours
 * spread evenly over a month and thirty-two hours in one frantic Tuesday are
 * the same row and completely different facts, and only one of them is a
 * resourcing problem. That is the whole job, so the chart is a shape and the
 * numbers stay in the table.
 *
 * ── One series, deliberately ────────────────────────────────────────────────
 *
 * Billable is a subset of tracked, not a second category — stacking them would
 * double-count and a second axis would be worse. So the column is tracked
 * hours and the tooltip carries billable alongside it. One series needs no
 * legend; the caption names it.
 *
 * ── Colour ─────────────────────────────────────────────────────────────────
 *
 * Two steps of the brand hue, chosen per theme rather than flipped: the brand
 * green sits at L 0.785 against the dark surface, outside the band where a mark
 * reads as a mark. Both steps were run through the palette validator against
 * their own surface — #4d8f22 on white, #5aa62f on #161d17 — and both pass
 * lightness, chroma and contrast.
 */
export default function HoursChart({ points, height = 180, label = "Tracked hours" }) {
  const theme = useTheme()
  const [hover, setHover] = useState(null)

  const dark = theme.palette.mode === "dark"
  const ink = dark ? "#5aa62f" : "#4d8f22"
  const surface = theme.palette.background.paper

  if (!points || points.length === 0) return null

  const peak = points.reduce((most, point) => Math.max(most, point.value), 0)
  /* A flat zero range would divide by zero and draw full-height bars for
     nothing at all, which reads as a busy period rather than an empty one. */
  const scale = peak > 0 ? peak : 1

  /*
   * A viewBox rather than pixel maths.
   *
   * The chart has to survive a sidebar opening, a phone rotating and a card
   * that is 40% of the row on one breakpoint and 100% on another. Laying it out
   * in user units and letting the browser scale it is the only version of that
   * which does not need a resize observer.
   */
  const W = 1000
  const H = 300
  const gap = points.length > 40 ? 1 : points.length > 20 ? 2 : 4
  const slot = W / points.length
  const barWidth = Math.max(1, slot - gap)
  /* 4px at the rendered size, scaled into user units — the rounded data-end
     the design system puts on every bar. Never more than half the width, or a
     thin bar turns into a lozenge. */
  const radius = Math.min(barWidth / 2, (4 * W) / 1000)

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        component="svg"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${label} over ${points.length} ${points.length === 1 ? "period" : "periods"}, peaking at ${points.find((p) => p.value === peak)?.hint || ""}`}
        sx={{ width: "100%", height, display: "block", overflow: "visible" }}
        onMouseLeave={() => setHover(null)}
      >
        {/* A baseline, not a grid. The column heights carry the comparison and
            a ruled background competes with them for the same ink. */}
        <line x1="0" y1={H} x2={W} y2={H} stroke={theme.palette.divider} strokeWidth="2" vectorEffect="non-scaling-stroke" />

        {points.map((point, index) => {
          const barHeight = (point.value / scale) * (H - 8)
          const x = index * slot + gap / 2
          const active = hover === index
          return (
            <g key={point.key}>
              {/* The hit target is the whole column, floor to ceiling. A 3px
                  bar with a 3px hover target is a tooltip nobody can open. */}
              <rect
                x={index * slot}
                y={0}
                width={slot}
                height={H}
                fill="transparent"
                onMouseEnter={() => setHover(index)}
              />
              <rect
                x={x}
                y={H - barHeight}
                width={barWidth}
                height={Math.max(barHeight, point.value > 0 ? 2 : 0)}
                rx={radius}
                fill={ink}
                opacity={hover === null || active ? 1 : 0.45}
                style={{ transition: "opacity 140ms ease" }}
                pointerEvents="none"
              />
              {/* A ring in the surface colour on the hovered column, which is
                  what keeps it legible when it abuts its neighbours. */}
              {active ? (
                <rect
                  x={x}
                  y={H - barHeight}
                  width={barWidth}
                  height={Math.max(barHeight, 2)}
                  rx={radius}
                  fill="none"
                  stroke={surface}
                  strokeWidth="2"
                  vectorEffect="non-scaling-stroke"
                  pointerEvents="none"
                />
              ) : null}
            </g>
          )
        })}
      </Box>

      {/* Only the two ends are labelled. A tick under every column is a row of
          unreadable dates, and the tooltip answers "which day is this". */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {points[0]?.hint}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {points[points.length - 1]?.hint}
        </Typography>
      </Box>

      {hover !== null && points[hover] ? (
        <Paper
          elevation={8}
          sx={{
            position: "absolute",
            top: 0,
            /* Clamped to the card, so the last column's tooltip does not hang
               off the right edge where it cannot be read. */
            left: `clamp(0px, ${((hover + 0.5) / points.length) * 100}% - 70px, calc(100% - 140px))`,
            width: 140,
            p: 1,
            pointerEvents: "none",
            borderRadius: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            {points[hover].hint}
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {points[hover].display}
          </Typography>
          {points[hover].secondary ? (
            <Typography variant="caption" color="text.secondary">
              {points[hover].secondary}
            </Typography>
          ) : null}
        </Paper>
      ) : null}
    </Box>
  )
}
